import {skipToken} from '@reduxjs/toolkit/query';

import {useState} from 'react';

import {equals, isNil} from 'ramda';

import {newVehicleApi, useGetModelYearQuery} from '@dms/api/new-vehicle';

import type {
  NewPricelistVersionDialogProps,
  PublishedPriceChange,
} from './NewPricelistVersionDialog';

const ZERO_PRICE = '0.00';

const isSamePrice = (price: string, storedPrice: string) => Number(price) === Number(storedPrice);

/**
 * A published model year refuses a direct price change (DMS-NV-CT-0054). Its editors save
 * everything else first, then hand the price to NewPricelistVersionDialog as a mid-year adjustment.
 */
export function usePublishedPriceSave(modelYearId: string | undefined) {
  const {data: modelYear} = useGetModelYearQuery(isNil(modelYearId) ? skipToken : {modelYearId});
  const [getModelYear] = newVehicleApi.useLazyGetModelYearQuery();
  const [change, setChange] = useState<PublishedPriceChange>();

  /**
   * On a published year, runs `saveFirst` and opens the confirm with the model-year version that
   * save produced. Resolves false on a draft year, before anything is sent.
   */
  const confirmAdjustment = async (
    adjustment: PublishedPriceChange['adjustment'],
    options: {
      saveFirst?: {saved: NonNullable<PublishedPriceChange['saved']>; run: () => Promise<unknown>};
      onDone: () => void;
    }
  ) => {
    if (isNil(modelYearId)) {
      return false;
    }
    // Waits for the first load, so a save made before the status is known is not sent directly.
    const known = await getModelYear({modelYearId}, true).unwrap();
    if (known.status !== 'active') {
      return false;
    }
    await options.saveFirst?.run();
    setChange({
      adjustment,
      modelYear: options.saveFirst ? await getModelYear({modelYearId}, false).unwrap() : known,
      saved: options.saveFirst?.saved,
      onDone: options.onDone,
    });
    return true;
  };

  /**
   * An editor save. A changed price on a published year is held back: the other changed fields save
   * with the stored price, so their errors stay in the editor, then the confirm sets the price.
   * Resolves true when the confirm took over; otherwise the whole save ran and `onDone` was called.
   */
  const save = async <V extends Record<K, string>, K extends keyof V>(form: {
    values: V;
    /** The record as the server holds it; the price compares against this one. */
    storedValues: V;
    priceField: K;
    /** A save that stopped half way runs again, even when no field differs from the stored values. */
    isSavePending?: boolean;
    scope: PublishedPriceChange['adjustment']['scope'];
    resourceId: string;
    save: (values: V, options: {isPriceHeldBack: boolean}) => Promise<unknown>;
    onDone: () => void;
  }) => {
    const storedPrice = form.storedValues[form.priceField];
    const otherValues: V = {...form.values, [form.priceField]: storedPrice};
    const isConfirming =
      !isSamePrice(form.values[form.priceField], storedPrice) &&
      (await confirmAdjustment(
        {
          kind: 'set',
          value: form.values[form.priceField] || ZERO_PRICE,
          scope: form.scope,
          resourceIds: [form.resourceId],
        },
        {
          saveFirst:
            form.isSavePending || !equals(otherValues, form.storedValues)
              ? {
                  saved: 'otherChanges',
                  run: () => form.save(otherValues, {isPriceHeldBack: true}),
                }
              : undefined,
          onDone: form.onDone,
        }
      ));
    if (!isConfirming) {
      await form.save(form.values, {isPriceHeldBack: false});
      form.onDone();
    }
    return isConfirming;
  };

  const dialogProps: NewPricelistVersionDialogProps = {
    change,
    onClose: () => setChange(undefined),
  };
  return {isPublished: modelYear?.status === 'active', save, confirmAdjustment, dialogProps};
}
